import { skipToken } from '@apollo/client/react'
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  TextField,
} from '@mui/material'
import type { TFunction } from 'i18next'
import { kebabCase } from 'lodash-es'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Disclosure,
  DisclosurePanel,
  Heading,
} from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { tv } from 'tailwind-variants'
import { match } from 'ts-pattern'
import IcBaselineKeyboardArrowRight from '~icons/ic/baseline-keyboard-arrow-right'
import { ImageSource } from 'src/components/Common/ImageSource'
import type { UseModelArtworkDataSourceOptions } from 'src/components/Common/MediaPicker'
import { ModelArtworkPicker } from 'src/components/Common/MediaPicker'
import {
  CropCurrentImageForUploadButton,
  dialogCropImage,
} from 'src/components/CropImage'
import { TagSelect } from 'src/components/Tag/TagSelect'
import { UILink } from 'src/components/ui/Link'
import { UISelect, UISelectItem } from 'src/components/ui/Select'
import Text from 'src/components/ui/Text'
import { GenerationModelType } from 'src/services/apollo/generated/graphql'
import { useGetGenerationModelTotalCount } from 'src/services/apollo/generation-model'
import { useMyMembership } from 'src/services/apollo/membership'
import { useLoraCategories } from 'src/stores/model'
import { cx } from 'src/utils/cx'
import { isLora, isDitV1OrMMDIT26AModel, isUserLora } from 'src/utils/model'
import { startEventGroup, track } from 'src/utils/tracker'
import type { HandleChange } from 'src/utils/useForm'
import { userHasMembership } from 'src/utils/user'
import { modelPermissions } from '../../constants'
import { UploadImagePlacerholder } from './ModelAssetForm'
import type { ModelSubmitFormValues } from './ModelSubmitFormProvider'

function RequiredMark() {
  return (
    <span className="text-red-500 ml-1" title="Required">
      *
    </span>
  )
}

const getSelectionLabel = (
  t: TFunction,
  type: GenerationModelType | undefined,
) =>
  match(type)
    .with(
      GenerationModelType.Lora,
      GenerationModelType.UnknownLora,
      GenerationModelType.MultiLora,
      () => t('market:model.type.lora'),
    )
    .with(GenerationModelType.UnknownModel, () => t('generation:model.label'))
    .with(GenerationModelType.SdV1Model, () => t('market:model.type.sdv1model'))
    .with(GenerationModelType.SdxlLora, () => t('market:model.type.sdxllora'))
    .with(GenerationModelType.SdxlModel, () => t('market:model.type.sdxlmodel'))
    // Is this really needed?
    .otherwise(() => t('market:model.type.unknown'))

const allowedModelTypes = [
  GenerationModelType.UnknownLora,
  GenerationModelType.UnknownModel,
]

const headingStyle = tv({
  base: 'col-span-full mt-8 first:mt-0 text-base font-semibold text-black dark:text-white',
})

export default function ModelInfoForm(props: {
  isEditing?: boolean
  values: ModelSubmitFormValues
  handleChange: HandleChange<keyof ModelSubmitFormValues>
  className?: string
  hidePrivateOption?: boolean
  appendComp?: JSX.Element
  highlightId?: string
}) {
  const { values, handleChange, isEditing } = props
  const [, setCoverUploading] = useState(false)
  const [coverMedia, setCoverMedia] = useState(() => values.media)
  const { t } = useTranslation([
    'market',
    'common',
    'action',
    'artwork',
    'generation',
  ])

  const {
    title,
    extra,
    tags,
    isDownloadable,
    isPrivate,
    type,
    agreeCoverImage,
    agreeNonMinor,
    loraBaseModelTypes,
  } = values

  // Check if this is a DIT/MMDIT26A-only LoRA (all base model types are in the DIT family)
  const isDitOnlyLora =
    loraBaseModelTypes &&
    loraBaseModelTypes.length > 0 &&
    loraBaseModelTypes.every(isDitV1OrMMDIT26AModel)
  // Check if this LoRA has mixed versions (some DIT-family, some non-DIT-family)
  const hasDitVersions =
    loraBaseModelTypes && loraBaseModelTypes.some(isDitV1OrMMDIT26AModel)

  // actual model info, without being affected by form changes
  const originalFormValuesRef = useRef<ModelSubmitFormValues | undefined>(
    values,
  )
  useEffect(() => {
    originalFormValuesRef.current = values
  }, [values.modelId])
  const originalFormValues = originalFormValuesRef.current ?? values

  /*
   * Highlight
   */

  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (props.highlightId) {
      const el = rootRef.current?.querySelector<HTMLElement>(
        `[data-highlight-id="${props.highlightId}"]`,
      )
      if (el) {
        el.dataset.highlighted = ''
      }
    }
  }, [props.highlightId])

  /*
   * LoRA category
   */

  const loraCategories = useLoraCategories()

  /*
   * Model Artwork Picker
   */

  const modelArtworkPickerOptions: UseModelArtworkDataSourceOptions =
    useMemo(() => {
      const modelOptions = isLora(values.type)
        ? {
            loraId: values.modelId,
          }
        : {
            modelId: values.modelId,
          }

      return {
        ...modelOptions,
        first: 30,
        isSafeSearch: true,
        orderBy: '-markInfo.likedCount',
      } as UseModelArtworkDataSourceOptions
    }, [values.modelId, values.type])

  /*
   * Render
   */

  return (
    <div
      ref={rootRef}
      className={cx('@container/form', 'text-sm', props.className)}
    >
      <div className="grid grid-cols-1 @[600px]/form:grid-cols-[2fr_1fr] grid-flow-dense gap-4">
        {isEditing && (
          <div className="@[800px]/form:col-start-2 flex flex-col gap-2">
            <span className="text-primary">
              {t('market:submit.model-cover.title')}
            </span>
            <ImageSource
              className="relative"
              size="md"
              value={coverMedia}
              onChange={m => {
                if (!m) return
                setCoverMedia(m)
                handleChange('media')(m)
              }}
              onUpload={setCoverUploading}
              placeholderElement={<UploadImagePlacerholder />}
              supportedMimeTypes={['image/jpeg', 'image/png', 'image/webp']}
              dropzoneOptions={{
                noKeyboard: true,
              }}
              preHandleImage={async file => {
                const parsed = await dialogCropImage({ file })
                return (parsed as File) ?? file
              }}
              fileHandleElement={({ action }) => (
                <div className="absolute bottom-4 font-sans">
                  <CropCurrentImageForUploadButton
                    media={values.media}
                    action={action}
                  />
                </div>
              )}
              features={{
                dragDrop: true,
                uploadButton: true,
                taskPicker: false,
                customPicker: true,
                nativeGallery: false,
                nativeCamera: false,
                buttons: true,
              }}
              buttonProps={{
                customPicker: {
                  variant: 'filled',
                },
              }}
              customPickerLabel={t(
                'action:image-source.media-picker.model-artwork.button',
                {
                  context: isLora(values.type) ? 'lora' : undefined,
                },
              )}
              customPickerElement={(open, onPick, onCancel) => (
                <ModelArtworkPicker
                  title={t(
                    'action:image-source.media-picker.model-artwork.title',
                  )}
                  open={open}
                  onSubmit={onPick}
                  onClose={onCancel}
                  dataSourceOptions={modelArtworkPickerOptions}
                />
              )}
            />
          </div>
        )}
        <div className="col-start-1 flex flex-col gap-4 mt-1">
          <div className="flex flex-col gap-2">
            <span className="text-primary">
              <Text i18nKey="market:submit.model-form.name" />
            </span>
            <TextField
              size="small"
              value={title}
              onChange={handleChange('title')}
              required
            />
          </div>
          {!isUserLora(values.type) && (
            <div className="flex flex-col gap-2">
              <span className="text-primary">
                <Text i18nKey="market:submit.model-type" />
                <RequiredMark />
              </span>
              <UISelect
                // For alignment
                buttonClassName="w-full h-[40px] pl-[8.5px]"
                popoverClassName="w-[--trigger-width]"
                selectValueClassName="data-[placeholder]:text-sm"
                selectedKey={values.type ?? ''}
                onSelectionChange={key => {
                  handleChange('type')(key)
                  if (!isLora(key as string)) {
                    handleChange('category')(null)
                  }
                }}
                isDisabled={isEditing}
              >
                {(isEditing ? [values.type] : allowedModelTypes).map(m => (
                  <UISelectItem key={m} id={m} labelClassName="text-sm">
                    {getSelectionLabel(t, m)}
                  </UISelectItem>
                ))}
              </UISelect>
            </div>
          )}
          {isLora(values.type) && (
            <div
              data-highlight-id="category-field"
              className={cx(
                'flex flex-col gap-2',
                'p-2 -m-2 rounded-xl data-[highlighted]:bg-amber-100 dark:data-[highlighted]:bg-amber-500/15',
              )}
            >
              <span className="text-primary">
                <Text i18nKey="common:category.label" />
              </span>
              <UISelect
                // For alignment
                buttonClassName="w-full h-[40px] pl-[8.5px]"
                popoverClassName="w-[--trigger-width]"
                selectValueClassName="data-[placeholder]:text-sm"
                selectedKey={values.category ?? null}
                onSelectionChange={k => {
                  handleChange('category')(k as string)
                }}
                placeholder={t('market:submit.model-form.category.placeholder')}
                aria-label={t('common:category.label')}
                isRequired
              >
                {loraCategories.map(it =>
                  it.value ? (
                    <UISelectItem
                      key={it.value}
                      id={it.value}
                      labelClassName="text-sm"
                      textValue={it.label}
                    >
                      <Text i18nKey={it.label} defaults={it.label} />
                    </UISelectItem>
                  ) : null,
                )}
              </UISelect>
            </div>
          )}
          <div className="flex flex-col gap-2">
            <span className="text-primary">
              <Text i18nKey="artwork:tags" />
            </span>
            <TagSelect
              value={tags || []}
              onTagSelected={tag =>
                handleChange('tags')([...(tags || []), tag])
              }
              onTagRemove={tag =>
                handleChange('tags')((tags || []).filter(t => t.id !== tag.id))
              }
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-primary">
              <Text i18nKey="market:submit.model-form.description" />
            </span>
            <TextField
              size="small"
              value={extra?.description || ''}
              onChange={handleChange('extra.description')}
              multiline
              rows={3}
            />
          </div>
        </div>
        {/* Private model option */}
        {!props.hidePrivateOption && (
          <>
            <h2 className={headingStyle()}>
              <Text i18nKey="common:visibility.label" />
            </h2>
            <div className="col-start-1">
              <PrivateModelToggle
                modelType={type}
                handleChange={handleChange}
                isPrivate={isPrivate}
                isEditing={isEditing}
                originalFormValues={originalFormValues}
              />
            </div>
          </>
        )}

        {/* Permission options */}
        <Disclosure className="col-span-full flex flex-col mt-8 first:mt-0">
          <Heading className={headingStyle()}>
            <Button
              slot="trigger"
              className="group inline-flex items-center gap-1 outline-none"
            >
              <span>
                <Text i18nKey="market:submit.permissions" />
              </span>
              <IcBaselineKeyboardArrowRight className="size-5 transition-transform group-aria-expanded:rotate-90" />
            </Button>
          </Heading>

          <DisclosurePanel className="flex flex-col">
            <FormControl disabled={isDitOnlyLora}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDownloadable}
                    onChange={(_, checked) =>
                      handleChange('isDownloadable')(checked)
                    }
                  />
                }
                label={
                  <span className="flex flex-col">
                    {t('market:submit.allow-download')}
                    {hasDitVersions && (
                      <span className="text-xs text-neutral-500 dark:text-neutral-400">
                        ({t('market:submit.dit-lora-download-not-supported')})
                      </span>
                    )}
                  </span>
                }
              />
            </FormControl>
            {modelPermissions.map(p => (
              <FormControl key={p}>
                <FormControlLabel
                  checked={extra?.permissions?.[p] ?? false}
                  onChange={(_, checked) => {
                    handleChange(`extra.permissions.${p}`)(checked)
                  }}
                  control={<Checkbox />}
                  label={t(`market:model.permissions.${kebabCase(p)}`)}
                />
              </FormControl>
            ))}
          </DisclosurePanel>
        </Disclosure>

        {/* Agreements */}
        {!isEditing && (
          <>
            <h2 className={headingStyle()}>
              <Text i18nKey="market:submit.agreements.title" />
            </h2>
            <div className="col-span-full flex flex-col gap-2">
              <FormControl>
                <FormControlLabel
                  className="items-start"
                  control={
                    <Checkbox
                      checked={agreeNonMinor}
                      onChange={(_, checked) =>
                        handleChange('agreeNonMinor')(checked)
                      }
                    />
                  }
                  label={t('market:submit.agreements.agree-non-minor')}
                />
              </FormControl>
              <FormControl>
                <FormControlLabel
                  className="items-start"
                  control={
                    <Checkbox
                      checked={agreeCoverImage}
                      onChange={(_, checked) =>
                        handleChange('agreeCoverImage')(checked)
                      }
                    />
                  }
                  label={t('market:submit.agreements.agree-cover-image')}
                />
              </FormControl>
            </div>
          </>
        )}
        {props.appendComp}
      </div>
    </div>
  )
}

function PrivateModelToggle(props: {
  modelType?: GenerationModelType
  handleChange: HandleChange<any, string>
  isPrivate?: boolean
  isEditing?: boolean
  originalFormValues?: ModelSubmitFormValues
}) {
  const { t } = useTranslation(['market'])
  const { modelType, isPrivate, isEditing, originalFormValues, handleChange } =
    props

  const { data } = useMyMembership()
  const { membership, id: userId } = data || {}
  const isMember = userHasMembership(data)
  const { data: modelTotalCounts } = useGetGenerationModelTotalCount(
    isMember && modelType
      ? {
          authorId: userId,
          type: isLora(modelType)
            ? GenerationModelType.AnyNonUserLora
            : GenerationModelType.AnyModel,
          isPrivate: true,
        }
      : skipToken,
  )

  const remainingCount =
    (membership?.privilege.privateModel ?? 1) - (modelTotalCounts ?? 0)

  // TODO: reject changing public model to private in backend
  const shouldDisable =
    !modelType ||
    remainingCount < 1 ||
    (isEditing && !originalFormValues?.isPrivate)

  const interpolationObj = {
    number: remainingCount,
  }

  const membershipInfo = !modelType ? null : isMember ? (
    <p>
      {isLora(modelType)
        ? t('market:submit.private.how-many-lora-slots-left', interpolationObj)
        : t(
            'market:submit.private.how-many-model-slots-left',
            interpolationObj,
          )}
    </p>
  ) : (
    <>
      <span>{t('market:submit.private.join-to-upload-more')}</span>
      <UILink
        href="/membership/plans"
        className="ml-3 px-1 py-0.5 text-theme-primary rounded-sm"
        onPress={() => {
          const trackingLocation = 'market-submit'
          track('enter_member_click', { location: trackingLocation })
          startEventGroup('member_source', trackingLocation, {
            persistent: true,
          })
        }}
      >
        {t('market:submit.private.join-member')}
      </UILink>
    </>
  )

  return (
    <div className="flex flex-col">
      <FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={isPrivate}
              onChange={(_, checked) => handleChange('isPrivate')(checked)}
            />
          }
          // disallow editing private option without selecting a type
          disabled={shouldDisable}
          label={
            <div className="flex items-center">
              <span>{t('market:submit.model-form.private-model')}</span>
              <span className="ml-2 px-1.5 text-xs leading-5 uppercase tracking-wide text-white text-shadow-xs shadow-black/50 bg-gradient-purple-pink /bg-gradient-to-br /from-fuchsia-500 /to-purple-600 rounded-full">
                Membership
              </span>
            </div>
          }
        />
      </FormControl>
      <div className="pl-8">{membershipInfo}</div>
    </div>
  )
}
